// Phase 11f — operator-side caregiver-dispute bandage per 11-7(a)+(b).
//
// `GET /admin/caregivers/:id/payout-corrections` returns the audit-event
// history of corrective operator actions on this caregiver's payouts,
// pre-filtered to the 6 action prefixes that represent "operator did
// something corrective" per 11-7(a). The bandage exists at v1 as a
// scaffolding read endpoint; Phase 20's Dispute Resolution Center
// absorbs it via `/admin/disputes?partyType=CAREGIVER` per 11f-5.
//
// Storage model: no new entity, no new column — the underlying data
// IS the existing audit-event log per 11-7(a). The endpoint is purely
// a read projection.
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────────────────
// shared
// ─────────────────────────────────────────────────────────────────────────────
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
/** The 6 audit-event actions filtered by the bandage per 11-7(b). */
export const PayoutCorrectionActionSchema = z.enum([
    'payout.voided',
    'payout.regenerated',
    'payout.recorded',
    'payout.manual_adjustment_added',
    'payout.eft_rejected',
    'payout.reissued_via_fallback',
]);
// ─────────────────────────────────────────────────────────────────────────────
// GET /admin/caregivers/:id/payout-corrections — query + response
// ─────────────────────────────────────────────────────────────────────────────
/** Per 11f-4 defaults: fromDate=12 months ago, toDate=now, page=1,
 *  pageSize=50. Both date fields are optional — service supplies the
 *  defaults at query time. */
export const AdminPayoutCorrectionsListQuerySchema = z.object({
    fromDate: IsoDateSchema.optional(),
    toDate: IsoDateSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(50),
});
export const AdminPayoutCorrectionRowSchema = z.object({
    id: z.string().uuid(),
    action: PayoutCorrectionActionSchema,
    payoutId: z.string().uuid(),
    payoutNumber: z.string().nullable(),
    payoutCycleStartDate: IsoDateSchema.nullable(),
    /** Sanitised payload: the audit-event payload as recorded, with the
     *  base fields (`caregiverId`, `payoutId`, `payoutNumber`) removed
     *  since they're already at the top level. Frontend uses this for
     *  the per-action context display. */
    payload: z.record(z.unknown()),
    operatorUserId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
});
export const AdminPayoutCorrectionsListResponseSchema = z.object({
    items: z.array(AdminPayoutCorrectionRowSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    /** The actual window used for the query — useful for the frontend
     *  to render "showing corrections from <start> to <end>". When the
     *  caller omits fromDate / toDate, these reflect the defaults. */
    fromDate: IsoDateSchema,
    toDate: IsoDateSchema,
});
//# sourceMappingURL=payout-corrections-admin.schema.js.map